import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutDashboard, Inbox, CalendarDays, FileText, Wallet, Banknote, UserCog,
  MessageSquare, Star, Bell, HelpCircle, LogOut, Search, MapPin, ChevronDown,
  Menu, Users, UserCheck, ClipboardList, FileCheck, Newspaper, BarChart3,
  Settings, Home, Heart, CreditCard, UserCircle, Clock, CheckCircle2, XCircle, Coins,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../ui/dropdown-menu";
import Logo from "../Logo";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { STATUS_LABELS, STATUS_STYLES } from "../../lib/format";

export const ICONS = {
  dashboard: LayoutDashboard, inbox: Inbox, calendar: CalendarDays, file: FileText,
  wallet: Wallet, banknote: Banknote, usercog: UserCog, chat: MessageSquare,
  star: Star, bell: Bell, help: HelpCircle, logout: LogOut, users: Users,
  usercheck: UserCheck, clipboard: ClipboardList, filecheck: FileCheck,
  newspaper: Newspaper, chart: BarChart3, settings: Settings, home: Home,
  heart: Heart, search: Search, creditcard: CreditCard, usercircle: UserCircle,
  loader: Clock, check: CheckCircle2, x: XCircle, coins: Coins,
};

function SidebarContent({ menu, dark, logoBadge, bottomCard, onNavigate, badges }) {
  const location = useLocation();
  return (
    <div className={`flex flex-col h-full ${dark ? "bg-[#0b1736] text-slate-300" : "bg-white text-slate-600 border-r border-slate-100"}`}>
      <div className="px-5 pt-5 pb-4">
        <Link to="/">
          <div className="flex items-center gap-2 flex-wrap">
            <Logo dark={dark} withTagline={false} size="sm" />
            {logoBadge && <span className="text-[10px] font-bold bg-violet-600 text-white rounded-md px-2 py-0.5">{logoBadge}</span>}
          </div>
        </Link>
      </div>
      <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto" data-testid="sidebar-nav">
        {menu.map((m) => {
          const Icon = ICONS[m.icon] || LayoutDashboard;
          const active = m.to && (m.exact ? location.pathname === m.to : location.pathname.startsWith(m.to));
          const badge = badges?.[m.badgeKey];
          return (
            <button key={m.label} onClick={() => onNavigate(m)} data-testid={`nav-${m.testId || m.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] font-semibold transition-colors ${
                active
                  ? dark ? "bg-blue-600 text-white shadow-lg shadow-blue-900/40" : "bg-blue-50 text-blue-600"
                  : dark ? "hover:bg-white/5 hover:text-white" : "hover:bg-slate-50 hover:text-slate-900"
              }`}>
              <Icon size={17} className="shrink-0" />
              <span className="flex-1 text-left">{m.label}</span>
              {badge > 0 && <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">{badge}</span>}
            </button>
          );
        })}
      </nav>
      {bottomCard && <div className="p-4">{bottomCard}</div>}
    </div>
  );
}

export default function DashboardShell({ menu, dark = false, logoBadge, bottomCard, onlinePill, locationPill, notifPath, badges, children }) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { data: notif } = useQuery({
    queryKey: ["notifications"], queryFn: () => api.get("/notifications").then((r) => r.data), refetchInterval: 15000,
  });
  const roleLabel = { pengguna: "Pengguna", inspector: "Inspector", admin: "Super Admin" }[user?.role] || "";

  const onNavigate = async (m) => {
    setSheetOpen(false);
    if (m.action === "logout") { await logout(); navigate("/"); return; }
    if (m.to) navigate(m.to);
  };

  return (
    <div className="min-h-screen bg-[#f7f9fc] font-sans">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-[248px] z-30">
        <SidebarContent menu={menu} dark={dark} logoBadge={logoBadge} bottomCard={bottomCard} onNavigate={onNavigate} badges={badges} />
      </aside>

      <div className="lg:ml-[248px] flex flex-col min-h-screen">
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-slate-100">
          <div className="flex items-center gap-3 px-4 sm:px-6 h-16">
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden p-2 -ml-2 text-slate-700" aria-label="menu" data-testid="mobile-menu-btn"><Menu size={22} /></button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-[268px]">
                <SidebarContent menu={menu} dark={dark} logoBadge={logoBadge} bottomCard={bottomCard} onNavigate={onNavigate} badges={badges} />
              </SheetContent>
            </Sheet>

            <div className="hidden md:flex items-center gap-2 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2 w-64">
              <Search size={15} className="text-slate-400" />
              <input placeholder="Cari di Inspecar" className="bg-transparent text-sm outline-none w-full text-slate-700 placeholder:text-slate-400" />
            </div>
            <span className="md:hidden font-extrabold text-slate-900">Inspecar</span>
            <div className="flex-1" />

            {locationPill && (
              <span className="hidden sm:flex items-center gap-1.5 text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg px-3 py-2">
                <MapPin size={14} className="text-slate-500" /> {locationPill}
              </span>
            )}
            <button onClick={() => notifPath && navigate(notifPath)} className="relative p-2 text-slate-500 hover:text-slate-800 transition-colors" aria-label="notifikasi" data-testid="notif-bell">
              <Bell size={19} />
              {notif?.unread > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notif-unread-count">{notif.unread}</span>
              )}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2.5 outline-none" data-testid="user-menu-trigger">
                  <img src={user?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || "U")}&background=2563eb&color=fff`} alt={user?.name} className="w-9 h-9 rounded-full object-cover" />
                  <div className="hidden sm:block leading-tight text-left">
                    <p className="text-[13px] font-bold text-slate-900" data-testid="topbar-user-name">{user?.name}</p>
                    <p className="text-[11px] text-slate-400 font-medium">{roleLabel}</p>
                  </div>
                  <ChevronDown size={14} className="text-slate-400 hidden sm:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5 text-xs text-slate-500 truncate">{user?.email}</div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/")}><Home size={14} className="mr-2" /> Beranda</DropdownMenuItem>
                <DropdownMenuItem onClick={() => onNavigate({ action: "logout" })} data-testid="logout-menu-item" className="text-red-600"><LogOut size={14} className="mr-2" /> Keluar</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {onlinePill !== undefined && (
          <div className="flex justify-end px-4 sm:px-6 pt-4">
            <OnlineToggle value={onlinePill.value} onChange={onlinePill.onChange} />
          </div>
        )}

        <main className="flex-1 px-4 sm:px-6 py-5 max-w-[1400px] w-full mx-auto">{children}</main>
      </div>
    </div>
  );
}

export function OnlineToggle({ value, onChange }) {
  return (
    <button onClick={() => onChange(!value)} data-testid="online-toggle"
      className={`flex items-center gap-2 rounded-full pl-3 pr-4 py-1.5 text-xs font-bold border transition-colors ${
        value ? "bg-emerald-50 text-emerald-600 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"
      }`}>
      <span className={`w-2 h-2 rounded-full ${value ? "bg-emerald-500" : "bg-slate-400"}`} />
      {value ? "Online" : "Offline"}
      <ChevronDown size={13} />
    </button>
  );
}

export function StatCard({ label, value, icon, color, delta, testId }) {
  const Icon = ICONS[icon] || LayoutDashboard;
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 hover:shadow-md transition-shadow" data-testid={testId}>
      <div className="flex items-center gap-3">
        <span className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${color}`}><Icon size={20} /></span>
        <p className="text-xs font-semibold text-slate-400 leading-tight">{label}</p>
      </div>
      <div className="mt-3 flex items-end gap-2">
        <p className="text-2xl font-extrabold text-slate-900">{value}</p>
        {delta && <span className="text-[11px] font-bold text-emerald-500 mb-1">↑ {delta.replace("+", "")}</span>}
      </div>
    </div>
  );
}

export function Card({ title, link, onLink, children, className = "", action }) {
  return (
    <div className={`bg-white rounded-2xl border border-slate-100 shadow-sm p-5 ${className}`}>
      {(title || link || action) && (
        <div className="flex items-center justify-between mb-4 gap-2">
          <h3 className="text-sm font-extrabold text-slate-900">{title}</h3>
          {link && <button onClick={onLink} className="text-xs font-semibold text-blue-600 hover:text-blue-700">{link} &gt;</button>}
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

export function StatusPill({ status }) {
  return (
    <span className={`inline-block text-[11px] font-bold rounded-full px-2.5 py-1 whitespace-nowrap ${STATUS_STYLES[status] || "bg-slate-100 text-slate-600"}`} data-testid={`status-pill-${status}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900" data-testid="page-title">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function EmptyState({ icon = "clipboard", title, desc, action }) {
  const Icon = ICONS[icon] || ClipboardList;
  return (
    <div className="text-center py-12" data-testid="empty-state">
      <span className="inline-flex w-14 h-14 rounded-2xl bg-slate-50 items-center justify-center text-slate-300 mb-3"><Icon size={26} /></span>
      <p className="font-bold text-slate-700">{title}</p>
      {desc && <p className="text-sm text-slate-400 mt-1">{desc}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Spinner({ className = "" }) {
  return <div className={`flex justify-center py-10 ${className}`}><span className="w-8 h-8 rounded-full border-4 border-blue-100 border-t-blue-600 animate-spin" /></div>;
}
